import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import orderService from '../services/orderService';
import { useAppSelector } from '../store/hooks';
import type { Order, OrderItem } from '../types';
import { useTranslation } from 'react-i18next';

function OrderDetailPage() {
  const { id } = useParams<{ id: string }>(); // Obtener el ID del pedido de la URL
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useAppSelector((s) => s.auth.user);
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [shortLink, setShortLink] = useState<string | null>(null);
  const [longLink, setLongLink] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [linkType, setLinkType] = useState<'short' | 'long'>('short');

  const getStatusClasses = (status: string) => {
    switch (status) {
      case 'entregado': return 'bg-green-500/20 text-green-300 border border-green-500/30';
      case 'cancelado': return 'bg-red-500/20 text-red-300 border border-red-500/30';
      case 'pagado': return 'bg-blue-500/20 text-blue-300 border border-blue-500/30';
      case 'en_preparacion': return 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30';
      case 'listo_para_recoger': return 'bg-purple-500/20 text-purple-300 border border-purple-500/30';
      case 'en_camino': return 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30';
      default: return 'bg-orange-500/20 text-orange-300 border border-orange-500/30'; // Default para 'pendiente' u otros estados no definidos
    }
  };

  useEffect(() => {
    const fetchOrder = async () => {
      if (!user || !user.comercioKey) {
        setLoading(false);
        setError(t('orderDetail.errors.noCommerceKey'));
        return;
      }
      try {
        const data = await orderService.getOrderById(user.comercioKey, Number(id));
        setOrder(data);
      } catch (err: any) {
        setError(err.response?.data?.message || t('orderDetail.errors.loadDetail'));
        console.error('Error fetching order details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();

    const verifySession = async () => {
      const sessionId = new URLSearchParams(window.location.search).get('session_id');
      if (sessionId && user?.comercioKey && id) {
        setIsVerifying(true);
        try {
          await orderService.verifyPayment(user.comercioKey, Number(id), sessionId);
          // Refrescar los datos del pedido para mostrar el estado actualizado
          fetchOrder();
        } catch (err: any) {
          setError(t('orderDetail.errors.verify'));
          console.error('Error verifying payment:', err);
        } finally {
          setIsVerifying(false);
        }
      }
    };

    if (user?.comercioKey && id) {
      verifySession();
    }

  }, [id, user]);

  // Cuando el usuario cambia el tipo de enlace, actualizar el valor mostrado si ya existen
  useEffect(() => {
    if (shortLink || longLink) {
      setPaymentLink(linkType === 'short' ? (shortLink ?? null) : (longLink ?? null));
    }
  }, [linkType, shortLink, longLink]);

  const handleGeneratePaymentLink = async () => {
    if (!user || !user.comercioKey || !order) return;
    try {
      const [shortResp, longResp] = await Promise.all([
        orderService.createShortPaymentLink(user.comercioKey, order.id),
        orderService.createPaymentLink(user.comercioKey, order.id),
      ]);
      setShortLink(shortResp.short_url);
      setLongLink(longResp.url);
      setPaymentLink(linkType === 'short' ? shortResp.short_url : longResp.url);
      setCopySuccess(''); // Resetear mensaje de copia
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al generar el enlace de pago.');
      console.error('Error generating payment link:', err);
    }
  };

  const handleCopyLink = (): void => {
    if (paymentLink) {
      navigator.clipboard.writeText(paymentLink);
      setCopySuccess(t('orderDetail.actions.copied'));
    }
  };

  if (isVerifying) return <p className="text-center text-white/60 mt-8">{t('orderDetail.verifying')}</p>;
  if (loading) return <p className="text-center text-white/60 mt-8">{t('orderDetail.loading')}</p>;
  if (error) return <p className="text-center text-red-600 mt-8">{t('orderDetail.errorPrefix')}{error}</p>;
  if (!user || !user.comercioKey) return <p className="text-center text-white/60 mt-8">{t('orderDetail.needLogin')}</p>;
  if (!order) return <p className="text-center text-white/60 mt-8">{t('orderDetail.notFound')}</p>;

  return (
    <div className="space-y-6 max-w-7xl mx-auto dark-bg overflow-x-hidden">
      <button onClick={() => navigate(-1)} className="mb-4 btn-taratic btn-ghost-dark font-bold py-2 px-4 rounded-md">
        &larr; {t('orderDetail.back')}
      </button>
      <h1 className="text-3xl font-bold text-center mb-8 text-white">{t('orderDetail.titleN', { id: order.id })}</h1>

      <div className="bg-black/30 backdrop-blur-md p-6 rounded-3xl shadow-xl border border-white/20 mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-white">{t('orderDetail.sections.clientInfo')}</h2>
        <p><strong>{t('orderDetail.labels.client')}:</strong> {order.nombre_cliente}</p>
        <p><strong>{t('orderDetail.labels.phone')}:</strong> {order.telefono_cliente}</p>
        <p><strong>{t('orderDetail.labels.email')}:</strong> {order.email_cliente || t('common.na')}</p>
        <p><strong>{t('orderDetail.labels.deliveryAddress')}:</strong> {order.direccion_entrega || t('common.na')}</p>
        <p><strong>{t('orderDetail.labels.notes')}:</strong> {order.notas_cliente || t('common.na')}</p>
      </div>

      <div className="bg-black/30 backdrop-blur-md p-6 rounded-3xl shadow-xl border border-white/20 mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-white">{t('orderDetail.sections.orderDetails')}</h2>
        <p><strong>{t('orderDetail.labels.status')}:</strong> <span className={`py-1 px-3 rounded-full text-xs font-semibold ${getStatusClasses(order.estado)}`}>{order.estado.replace(/_/g, ' ').toUpperCase()}</span></p>
        <p><strong>{t('orderDetail.labels.type')}:</strong> {order.tipo}</p>
        <p><strong>{t('orderDetail.labels.channel')}:</strong> {order.canal || t('common.na')}</p>
        <p><strong>{t('orderDetail.labels.paymentMethod')}:</strong> {order.metodo_pago || t('common.na')}</p>
        <p><strong>{t('orderDetail.labels.total')}:</strong> <span className="text-xl font-bold text-green-400">{Number(order.total).toFixed(2)}€</span></p>
        <p><strong>{t('orderDetail.labels.createdAt')}:</strong> {new Date(order.created_at).toLocaleString()}</p>
      </div>

      <div className="bg-black/30 backdrop-blur-md p-6 rounded-3xl shadow-xl border border-white/20 mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-white">{t('orderDetail.sections.items')}</h2>
        {order.items && order.items.length > 0 ? (
          <ul className="divide-y divide-white/10">
            {order.items.map((item: OrderItem) => (
              <li key={item.item_id} className="py-4 flex items-center">
                {item.imagen_url && (
                  <img src={item.imagen_url} alt={item.nombre} className="w-16 h-16 object-cover rounded-md mr-4" />
                )}
                <div>
                  <p className="text-lg font-medium text-white">{item.nombre} (x{item.cantidad})</p>
                  <p className="text-white/60">{t('orderDetail.item.unitPrice', { price: Number(item.precio_unitario ?? 0).toFixed(2) })}</p>
                  <p className="text-white font-bold">{t('orderDetail.item.subtotal', { price: Number(item.subtotal ?? 0).toFixed(2) })}</p>
                </div>
              </li>
            ))}
          </ul>
        ) : (
<p className="text-white/60">{t('orderDetail.itemsEmpty')}</p>
        )}
      </div>

      <div className="bg-black/30 backdrop-blur-md p-6 rounded-3xl shadow-xl border border-white/20">
        <h2 className="text-2xl font-semibold mb-4 text-white">{t('orderDetail.sections.actions')}</h2>
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:space-x-4">
          <label className="text-sm font-medium text-white/80 mb-2 sm:mb-0">{t('orderDetail.actions.linkFormat')}</label>
          <div className="flex items-center space-x-4">
            <label className="inline-flex items-center space-x-2">
              <input
                type="radio"
                name="linkType"
                value="short"
                checked={linkType === 'short'}
                onChange={() => setLinkType('short')}
              />
              <span>{t('orderDetail.actions.shortRecommended')}</span>
            </label>
            <label className="inline-flex items-center space-x-2">
              <input
                type="radio"
                name="linkType"
                value="long"
                checked={linkType === 'long'}
                onChange={() => setLinkType('long')}
              />
              <span>{t('orderDetail.actions.longStripe')}</span>
            </label>
          </div>
        </div>
        {order.estado === 'pendiente' && (
          <button
            onClick={handleGeneratePaymentLink}
            className="w-full sm:w-auto flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white btn-taratic btn-green focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-300"
          >
            {t('orderDetail.actions.generateStripeLink')}
          </button>
        )}

        {paymentLink && (
          <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="w-full text-sm text-white/60">
              {linkType === 'short' ? t('orderDetail.actions.showingShort') : t('orderDetail.actions.showingLong')}
            </div>
            <input
              type="text"
              readOnly
              value={paymentLink}
              className="flex-grow px-4 py-2 border border-white/20 bg-black/20 text-white text-white/80 text-sm"
            />
            <button
              onClick={handleCopyLink}
              className="w-full sm:w-auto flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white btn-taratic btn-green focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-300"
            >
              {copySuccess || t('orderDetail.actions.copyLink')}
            </button>
            <a
              href={paymentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white btn-taratic btn-orange focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition duration-300"
            >
              {t('orderDetail.actions.openLink')}
            </a>
          </div>
        )}

        {order.estado !== 'pendiente' && (
          <div className="p-4 bg-black/20 rounded-md text-center">
            <p className="font-medium text-white/80">{t('orderDetail.actions.notPending')}</p>
            <p className="text-sm text-white/50">{t('orderDetail.actions.currentStatus')}: <span className={`font-semibold ${getStatusClasses(order.estado)}`}>{order.estado}</span></p>
          </div>
        )}

        {error && <p className="mt-4 text-red-600 text-center text-sm font-medium">{error}</p>}
      </div>
    </div>
  );
}

export default OrderDetailPage;
